// Read-only derived views of the game state for the UI (docs/08 §2.6).
import { B } from '../content/balance';
import { CHARMS, COLLECTORS, SYMBOLS } from '../content/registry';
import type { Decimal } from '../sim/decimal';
import { charmPrice, draftRerollPrice, hymnPrice, nextTribute, servicePrice, shopRerollPrice } from '../sim/intermission';
import { computeModifiers, type Modifiers } from '../sim/modifiers';
import { feedCoinsPrice, hungerActive, moodLuck, moodOf } from '../sim/hunger';
import { rotsIn } from '../sim/rot';
import { pityLuck } from '../sim/spin';
import { par, tribute } from '../sim/tribute';
import type { GameState, MoodId, ShopService } from '../sim/types';

let lastGame: GameState | null = null;
let lastMods: Modifiers | null = null;

export function modsOf(game: GameState): Modifiers {
  if (game !== lastGame || !lastMods) {
    lastGame = game;
    lastMods = computeModifiers(game.meta, game.run);
  }
  return lastMods;
}

export function currentLuck(game: GameState): { base: number; pity: number; total: number } {
  const run = game.run;
  const m = modsOf(game);
  if (!run) return { base: m.luck, pity: 0, total: m.luck };
  const pity = pityLuck(run.deadStreak, m.pityAfter);
  const base = m.luck + run.luckTemp + Math.floor((run.window.n - 1) / 5) * m.favoredLuckPer5 + moodLuck(run, m);
  return { base, pity, total: Math.max(0, base + pity) };
}

export interface HungerView {
  hunger: number;
  mood: MoodId;
  canFeed: boolean;
  feedCoinsPrice: Decimal;
}

/** Hunger and mood for the HUD; null before the machine has a face. */
export function hungerOf(game: GameState): HungerView | null {
  const run = game.run;
  const m = modsOf(game);
  if (!run || !hungerActive(m)) return null;
  return { hunger: run.hunger, mood: moodOf(run, m), canFeed: m.features.has('feed'), feedCoinsPrice: feedCoinsPrice(run, m) };
}

export function parOf(game: GameState): Decimal | null {
  const w = game.run?.window;
  return w ? par(w.tribute, w.spinsTotal) : null;
}

export interface Prices {
  next: Decimal;
  draftReroll: Decimal;
  shopReroll: Decimal;
  hymn: Decimal;
  charm: (id: string) => Decimal;
  sell: (id: string) => Decimal;
  service: (s: ShopService) => Decimal;
}

export function pricesOf(game: GameState): Prices | null {
  const run = game.run;
  if (!run) return null;
  const m = modsOf(game);
  return {
    next: nextTribute(run, m),
    draftReroll: draftRerollPrice(run, m),
    shopReroll: shopRerollPrice(run, m),
    hymn: hymnPrice(run, m),
    charm: (id) => charmPrice(run, m, id),
    sell: (id) => charmPrice(run, m, id).mul(B.CHARM_SELL).floor(),
    service: (s) => servicePrice(run, m, s),
  };
}

export interface DeckEntry {
  defId: string;
  count: number;
  chance: number;
  rarity: string;
  maxBonus: number;
  /** Fewest appearances left before one of these rots (null: doesn't rot). */
  rotsIn: number | null;
}

export function deckSummary(game: GameState): DeckEntry[] {
  const run = game.run;
  if (!run) return [];
  const m = modsOf(game);
  const by = new Map<string, DeckEntry>();
  for (const inst of run.deck) {
    const e = by.get(inst.defId) ?? { defId: inst.defId, count: 0, chance: 0, rarity: SYMBOLS.get(inst.defId)?.rarity ?? 'C', maxBonus: 0, rotsIn: null };
    e.count++;
    e.maxBonus = Math.max(e.maxBonus, inst.bonusValue);
    const left = rotsIn(inst.defId, inst.age, m);
    if (left !== null) e.rotsIn = Math.min(e.rotsIn ?? Infinity, left);
    by.set(inst.defId, e);
  }
  const n = run.deck.length;
  for (const e of by.values()) e.chance = e.count / n;
  return [...by.values()].sort((a, b) => b.count - a.count);
}

export function charmDef(id: string) {
  return CHARMS.get(id);
}

/** What the next window will ask for: T(n+1), with its Reckoning multiplier and The Loan. */
export function upcomingTribute(game: GameState): Decimal {
  const run = game.run!;
  const m = modsOf(game);
  const n = run.window.n + 1;
  const reck = run.intermission?.preview.find((p) => p.n === n);
  const mult = reck ? (COLLECTORS.get(reck.id)?.tributeMult ?? m.reckoningTributeMult) : 1;
  let t = tribute(n, m, mult);
  if (run.flags.loanNext) t = t.mul(run.flags.loanNext).ceil();
  return t;
}

/** Charms beyond the slot count sleep (Golden Handcuffs). */
export function charmAsleep(game: GameState, index: number): boolean {
  return index >= modsOf(game).charmSlots;
}
